/* ----------------------------------------------------------------------
   Cart reservation timer — pure urgency display (no inventory hold).

   IMPORTANT — why this lives in its own asset file instead of the snippet:
     The snippet (`snippets/cart-reservation-timer.liquid`) renders only
     when the cart is non-empty. If the customer loads the page with an
     empty cart, the snippet — and therefore any inline <script> inside
     it — is never emitted. When they then add their first item, cart.js
     does `cartDrawerBody.innerHTML = newHTML`, which DOES insert the
     element but does NOT execute any <script> tags in that HTML (browser
     security). The custom element ends up in the DOM as an unknown
     HTMLElement, connectedCallback never fires, no setInterval, no
     countdown.

     Loading this file unconditionally from `sections/cart-drawer.liquid`
     guarantees the custom element is registered the moment the page
     finishes parsing — regardless of cart state — so any later
     innerHTML-driven insertion auto-upgrades correctly and the timer
     starts ticking immediately on first add-to-cart.

   Persistence:
     The start timestamp is stored under `data-storage-key` in localStorage
     (set by the snippet, defaults to `arktic_cart_reservation_v1`). The
     timer reads it on every connectedCallback and computes remaining time
     from `Date.now()`, so the countdown survives across navigation,
     drawer open/close, and cart updates — the customer sees one
     continuous timer, not a reset on every action.

     When the timer reaches 0:00, it silently rewrites the start key and
     restarts. No cart action is taken (this is a UX display, not a
     real reservation).
---------------------------------------------------------------------- */
(function () {
  if (window.customElements && customElements.get('cart-reservation-timer')) return;

  class CartReservationTimer extends HTMLElement {
    connectedCallback() {
      this._minutes    = parseInt(this.dataset.minutes || '15', 10);
      this._storageKey = this.dataset.storageKey || 'cart_reservation_start';
      this._totalMs    = this._minutes * 60 * 1000;
      this._timeEl     = this.querySelector('[data-time]');
      if (!this._timeEl) return;

      this._initStart();
      this._tick();
      // 1-second interval is plenty for MM:SS display. Stable ref so
      // disconnectedCallback can clear it.
      this._interval = setInterval(() => this._tick(), 1000);
    }

    _initStart() {
      let start = 0;
      try {
        start = parseInt(localStorage.getItem(this._storageKey) || '0', 10);
      } catch (e) {
        // localStorage unavailable (private mode in some browsers) — fall
        // back to in-memory only. Timer effectively resets on every
        // drawer open in that case, which is acceptable.
        start = 0;
      }
      const now = Date.now();
      // If no timer running OR the previous one expired, start fresh.
      if (!start || (now - start) >= this._totalMs) {
        start = now;
        try { localStorage.setItem(this._storageKey, String(start)); } catch (e) {}
      }
      this._startTime = start;
    }

    _tick() {
      const now       = Date.now();
      const elapsed   = now - this._startTime;
      let   remaining = this._totalMs - elapsed;

      if (remaining <= 0) {
        // Timer expired — reset and continue. No cart action; this is a
        // pure urgency display.
        this._initStart();
        remaining = this._totalMs;
      }

      const totalSeconds = Math.floor(remaining / 1000);
      const mins = Math.floor(totalSeconds / 60);
      const secs = totalSeconds % 60;
      this._timeEl.textContent =
        mins + ':' + (secs < 10 ? '0' + secs : secs);
    }

    disconnectedCallback() {
      // Critical: cart.js replaces CartDrawerBody.innerHTML on every cart
      // update, which destroys this element and creates a new one. The
      // OLD interval has to be cleared or each cart update doubles up
      // active timers in memory, all racing to write the same DOM node
      // (which by then belongs to a new instance).
      if (this._interval) {
        clearInterval(this._interval);
        this._interval = null;
      }
    }
  }

  customElements.define('cart-reservation-timer', CartReservationTimer);
})();
